import type {
  Iso3166Alpha2,
  Iso3166Alpha3Lower,
  LocaleCode,
  LocaleDefinition,
  LocaleDomain,
  LocaleMetadata,
  LocaleRegistry,
} from "../types";

import { deriveLocaleMetadata } from "../country/derive-locale-metadata";
import { getSanityDocumentCountryCode } from "../country/get-sanity-document-country-code";
import { findLocaleCodeFromIso3166Alpha3Lower } from "./find-locale-code-from-iso3166-alpha3-lowercase";
import {
  DEFAULT_LOCALE_CODE,
  LOCALE_DEFINITIONS,
} from "./locale-registry-data";
import { validateLocaleRegistry } from "./validate-locale-registry";

const LOCALE_REGISTRY = validateLocaleRegistry(LOCALE_DEFINITIONS);

function getLocaleRegistry(): LocaleRegistry {
  return LOCALE_REGISTRY;
}

function getLocaleDefinition(code: LocaleCode): LocaleDefinition {
  const definition = LOCALE_REGISTRY[code];

  if (!definition) {
    throw new Error(`Unknown locale code: ${code}`);
  }

  return definition;
}

function getLocaleMetadata(code: LocaleCode): LocaleMetadata {
  getLocaleDefinition(code);
  return deriveLocaleMetadata(code);
}

function getCountryCodes(code: LocaleCode) {
  return getLocaleMetadata(code).country;
}

function getDomain(code: LocaleCode): LocaleDomain {
  return getLocaleMetadata(code).domain;
}

function getCanonicalOrigin(code: LocaleCode): string {
  return getLocaleMetadata(code).canonicalOrigin;
}

function getDefaultLocaleCode(): LocaleCode {
  return DEFAULT_LOCALE_CODE;
}

function getLocaleCodeFromIso3166Alpha3Lower(
  iso3166Alpha3Lower: Iso3166Alpha3Lower,
): LocaleCode {
  const code = findLocaleCodeFromIso3166Alpha3Lower(
    iso3166Alpha3Lower,
    LOCALE_DEFINITIONS,
  );

  if (!code) {
    throw new Error(
      `Unknown ISO 3166-1 alpha-3 lowercase country code: ${iso3166Alpha3Lower}`,
    );
  }

  return code;
}

function getLocaleCodeFromIso3166Alpha2(
  iso3166Alpha2: Iso3166Alpha2,
): LocaleCode {
  for (const definition of LOCALE_DEFINITIONS) {
    const metadata = deriveLocaleMetadata(definition.code);

    if (metadata.country.iso3166Alpha2 === iso3166Alpha2) {
      return definition.code;
    }
  }

  throw new Error(
    `Unknown ISO 3166-1 alpha-2 uppercase country code: ${iso3166Alpha2}`,
  );
}

export {
  getCanonicalOrigin,
  getCountryCodes,
  getDefaultLocaleCode,
  getDomain,
  getLocaleCodeFromIso3166Alpha2,
  getLocaleCodeFromIso3166Alpha3Lower,
  getLocaleDefinition,
  getLocaleMetadata,
  getLocaleRegistry,
  getSanityDocumentCountryCode,
};
